import { controls } from "@wordpress/data";
import { WEATHER_STORE } from "./store";
import { WeatherAction, WeatherSelect } from "./type";
import { IFetchPrayerResult } from "@/types/prayer-types";
import { IFecthWarningResult } from "@/types/warning-data";
import { ITidesfetchResult } from "@/types/tides-type";
import { IFetchObservationResult } from "@/types/observation-type";
import { IFetchWorldCitiesResult } from "@/types/world-cities-type";
import { IFetchWorldPrevisionResult } from "@/types/world-forecast-type";
import { IFetchImageryResult } from "@/types/imagery-type";
import { getClosestPosition } from "@/helpers/get-user-localization";
import { CityFeature } from "@/schemas/city-schema";
import { IFetchDailyForecastResult } from "@/types/daily-forecast-type";
import { IFetchPevision } from "@/types/prevision-type";
import { getForecastLocationIdParams } from "@/helpers/url-params";
import { LocationType } from "@/types/location-params-type";

/* ============================================================
   CITY PREVISION
============================================================ */

function* getCityPrevisionState(): Generator<any, void, any> {
	const paramsCityId = getForecastLocationIdParams(
		LocationType.KUWAIT_FORECAST,
	);

	const { cityId }: { cityId: string } = yield controls.select(
		WEATHER_STORE,
		"getCityLocationState",
	);

	yield controls.dispatch(WEATHER_STORE, "setForecastLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setForecastError", null);

	try {
		const result: IFetchPevision = yield {
			type: "FETCH_PREVISION",
			cityId: paramsCityId ?? cityId,
		};

		if (result.error) {
			yield controls.dispatch(WEATHER_STORE, "setForecastError", result.error);
			return;
		}

		yield controls.dispatch(WEATHER_STORE, "setLocation", {
			cityId,
			cityNameEn: result.data?.metadata.station_name_en
				.replace("Station", "")
				.trim(),
			cityNameAr: result.data?.metadata.station_name_ar,
		});
		yield controls.dispatch(WEATHER_STORE, "setForecastData", result.data);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setForecastLoading", false);
	}
}

getCityPrevisionState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_FORECAST_CITY_LOCATION";

/* ============================================================
   DAILY FORECAST
============================================================ */

function* getDailyForecast(): Generator<any, void, any> {
	const { stationIdSelected }: { stationIdSelected: string } =
		yield controls.select(WEATHER_STORE, "getDailyForecast");

	yield controls.dispatch(WEATHER_STORE, "setDailyForecastLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setDailyForecastError", null);

	try {
		const result: IFetchDailyForecastResult = yield {
			type: "FETCH_DAILY_FORECAST",
			stationIdSelected,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setDailyForecastError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setDailyForecastData",
			result.dayliesData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setDailyForecastLoading", false);
	}
}

getDailyForecast.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_DAILY_FORECAST_STATION_ID";

/* ============================================================
   DAILY MARINE
============================================================ */

function* getDailyMarineState(): Generator<any, void, any> {
	const { id }: { id: string } = yield controls.select(
		WEATHER_STORE,
		"getLocationMarine",
	);

	yield controls.dispatch(WEATHER_STORE, "setDailyMarineLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setDailyMarineError", null);

	try {
		const result: IFetchDailyForecastResult = yield {
			type: "FETCH_DAILY_MARINE_FORECAST",
			id,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setDailyMarineError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setDailyMarineData",
			result.dayliesData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setDailyMarineLoading", false);
	}
}

getDailyMarineState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_MARINE_LOCATION";

/* ============================================================
   PRAYER
============================================================ */

function* getPrayerState(): Generator<any, void, IFetchPrayerResult> {
	yield controls.dispatch(WEATHER_STORE, "setPrayerLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setPrayerError", null);

	try {
		const result = yield { type: "FETCH_PRAYER" };

		if (result.error) {
			yield controls.dispatch(WEATHER_STORE, "setPrayerError", result.error);
			return;
		}

		yield controls.dispatch(WEATHER_STORE, "setPrayData", result.prayerData);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setPrayerLoading", false);
	}
}

/* ============================================================
   WARNING
============================================================ */

function* getWarningState(): Generator<any, void, IFecthWarningResult> {
	yield controls.dispatch(WEATHER_STORE, "setWarningLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setWarningError", null);

	try {
		const result = yield { type: "FETCH_WARNING" };

		if (result.error) {
			yield controls.dispatch(WEATHER_STORE, "setWarningError", result.error);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setWarningData",
			result.warningData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setWarningLoading", false);
	}
}

/* ============================================================
   TIDES
============================================================ */

function* getTideState(): Generator<any, void, ITidesfetchResult> {
	yield controls.dispatch(WEATHER_STORE, "setTideLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setTideError", null);

	try {
		const result = yield { type: "FETCH_TIDE" };

		if (result.error) {
			yield controls.dispatch(WEATHER_STORE, "setTideError", result.error);
			return;
		}

		yield controls.dispatch(WEATHER_STORE, "setTideData", result.tidesData);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setTideLoading", false);
	}
}

/* ============================================================
   OBSERVATION
============================================================ */

function* getObservationStationState(): Generator<
	any,
	void,
	IFetchObservationResult
> {
	yield controls.dispatch(WEATHER_STORE, "setObservationLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setObservationError", null);

	try {
		const result = yield {
			type: "FETCH_OBSERVATIONS",
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setObservationError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setObservationData",
			result.stationData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setObservationLoading", false);
	}
}

getObservationStationState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_DAILY_FORECAST_STATION_ID";

function* getObservationImageryState(): Generator<
	any,
	void,
	IFetchImageryResult
> {
	const filter = yield controls.select(
		WEATHER_STORE,
		"getObservationFilterState",
	) as ReturnType<WeatherSelect["getObservationFilterState"]>;
	yield controls.dispatch(WEATHER_STORE, "setObservationImageryLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setObservationImageryError", null);

	try {
		const result = yield {
			type: "FETCH_OBSERVATION_IMAGERY",
			filter,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setObservationImageryError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setObservationImageryData",
			result.imageryData,
		);
	} finally {
		yield controls.dispatch(
			WEATHER_STORE,
			"setObservationImageryLoading",
			false,
		);
	}
}

getObservationImageryState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_OBSERVATION_FILTER";

/* ============================================================
   MARINE FORECAST
============================================================ */

function* getMarineForecastState(): Generator<any, void, any> {
	yield controls.dispatch(WEATHER_STORE, "setMarineForecastLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setMarineForecastError", null);

	const { id } = yield controls.select(WEATHER_STORE, "getLocationMarine");

	try {
		const result = yield {
			type: "FETCH_MARINE_FORECAST",
			id,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setMarineForecastError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setMarineForecastData",
			result.marineData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setMarineForecastLoading", false);
	}
}

getMarineForecastState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_MARINE_LOCATION";

/* ============================================================
   CITY LIST
============================================================ */

function* getCityState(): Generator<any, void, any> {
	yield controls.dispatch(WEATHER_STORE, "setForecastCityLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setForecastCityError", null);

	try {
		const { error, cityData } = yield { type: "FETCH_CITY" };

		if (error) {
			yield controls.dispatch(WEATHER_STORE, "setForecastCityError", error);
			return;
		}

		const {
			latitude,
			longitude,
		}: {
			latitude: number | null;
			longitude: number | null;
		} = yield { type: "GET_USER_POSITION" };

		if (latitude && longitude) {
			const userPositionCity: CityFeature | null = getClosestPosition({
				userPosition: {
					latitude,
					longitude,
				},
				kuwaitCity: cityData,
			});

			yield controls.dispatch(
				WEATHER_STORE,
				"setForecastUserLocation",
				userPositionCity
					? {
							cityId: userPositionCity?.properties.id,
							cityNameEn: userPositionCity?.properties.name_en,
							cityNameAr: userPositionCity?.properties.name_ar,
					  }
					: null,
			);
		}

		/** A ajouter si la geolocalisation doit etre la ville par default */

		// yield controls.dispatch(WEATHER_STORE, "setLocation", {
		// 	cityId: userPositionCity?.properties.id,
		// 	cityName: userPositionCity?.properties.label,
		// });

		yield controls.dispatch(WEATHER_STORE, "setForecastCityData", cityData);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setForecastCityLoading", false);
	}
}

/* ============================================================
   MARINE STATION 
============================================================ */

function* getMarineStationState(): Generator<any, void, any> {
	yield controls.dispatch(WEATHER_STORE, "setMarineStationLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setMarineStationError", null);

	try {
		const { error, marineStation } = yield { type: "FETCH_MARINE_STATION" };

		if (error) {
			yield controls.dispatch(WEATHER_STORE, "setMarineStationError", error);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setMarineStationData",
			marineStation,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setMarineStationLoading", false);
	}
}

/* ============================================================
   KUWAIT FORECAST
============================================================ */

function* getForecastKuwaitState(): Generator<any, void, any> {
	const {
		selectedDate,
	}: {
		selectedDate: Date;
	} = yield controls.select(WEATHER_STORE, "getForecastKuwaitState");

	yield controls.dispatch(WEATHER_STORE, "setForecastKuwaitLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setForecastKuwaitError", null);

	try {
		const result = yield {
			type: "FETCH_FORECAST_KUWAIT",
			selectedDate,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setForecastKuwaitError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setForecastKuwaitData",
			result.forecastData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setForecastKuwaitLoading", false);
	}
}

getForecastKuwaitState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_KUWAIT_FORECAST_DATE";

/* ============================================================
   WORLD FORECAST
============================================================ */

function* getWorldForecastPrevisionState(): Generator<any, void, any> {
	const { id = 0 }: { id: number } = yield controls.select(
		WEATHER_STORE,
		"getWorldLocationState",
	);

	yield controls.dispatch(WEATHER_STORE, "setWorldCitiesForecastLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setWorldCitiesForecastError", null);

	try {
		const result: IFetchWorldPrevisionResult = yield {
			type: "FETCH_WORLD_PREVISISION",
			cityId: id,
		};

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setWorldCitiesForecastError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setWorldCitiesForecastData",
			result.data,
		);
	} finally {
		yield controls.dispatch(
			WEATHER_STORE,
			"setWorldCitiesForecastLoading",
			false,
		);
	}
}

getWorldForecastPrevisionState.shouldInvalidate = (action: WeatherAction) =>
	action.type === "SET_WORLD_CITIES_LOCATION";

/* ============================================================
   WORLD CITIES
============================================================ */

function* getWorldCitiesState(): Generator<any, void, IFetchWorldCitiesResult> {
	yield controls.dispatch(WEATHER_STORE, "setWorldCitiesLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setWorldCitiesError", null);

	try {
		const result = yield { type: "FETCH_WORLD_CITIES" };

		if (result.error) {
			yield controls.dispatch(
				WEATHER_STORE,
				"setWorldCitiesError",
				result.error,
			);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setWorldCitiesData",
			result.worldCitiesData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setWorldCitiesLoading", false);
	}
}

function* getTimelineState(): Generator<any, void, any> {
	yield controls.dispatch(WEATHER_STORE, "setTimelineLoading", true);
	yield controls.dispatch(WEATHER_STORE, "setTimelineError", null);

	try {
		const result = yield { type: "FETCH_TIMELINE" };

		if (result.error) {
			yield controls.dispatch(WEATHER_STORE, "setTimelineError", result.error);
			return;
		}

		yield controls.dispatch(
			WEATHER_STORE,
			"setTimelineData",
			result.timelineData,
		);
	} finally {
		yield controls.dispatch(WEATHER_STORE, "setTimelineLoading", false);
	}
}

export const resolvers = {
	getCityPrevisionState,
	getForecastKuwaitState,
	getWorldForecastPrevisionState,
	getObservationStationState,
	getObservationImageryState,
	getDailyForecast,
	getMarineForecastState,
	getDailyMarineState,

	getPrayerState,
	getWarningState,
	getTideState,
	getCityState,
	getWorldCitiesState,
	getTimelineState,
	getMarineStationState,
};
